import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { OfferService } from '@/service/offer.service';
import { OfferWithImage } from '@/model/offer-with-image';
import { FeedbackResponse } from '@/model/feedback';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTableModule } from '@angular/material/table';
import { FeedbackDetailsComponent } from '@/app/components/feedback-details/feedback-details.component';
import { Location } from '@angular/common';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Category } from '@/model/category';
import { OfferType } from '@/model/offer-type';
import { GraphqlService } from '@/service/graphql.service';
@Component({
  selector: 'app-feedback-history',
  imports: [
    CommonModule,
    MatExpansionModule,
    MatBadgeModule,
    MatIconModule,
    MatChipsModule,
    MatTableModule,
    FeedbackDetailsComponent,
  ],
  templateUrl: './feedback-history.component.html',
  styleUrl: './feedback-history.component.scss',
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
  standalone: true,
})
export class FeedbackHistoryComponent implements OnInit {
  offer: OfferWithImage | undefined;
  offerId: string | undefined;
  history: FeedbackResponse[] = [];
  displayedColumns: string[] = ['status', 'date', 'comment', 'expand'];
  expandedElement: FeedbackResponse | null = null;

  categories: Category[] = [];
  types: OfferType[] = [];

  constructor(
    private route: ActivatedRoute,
    private offerService: OfferService,
    private graphqlService: GraphqlService,
    private location: Location,
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.offerId = params['id'];
      if (this.offerId) {
        this.loadOffer(this.offerId);
        this.loadHistory(this.offerId);
        this.loadCategories();
        this.loadTypes();
      }
    });
  }

  loadCategories() {
    this.graphqlService.allCategories().subscribe((data) => {
      this.categories = data;
    });
  }

  loadTypes() {
    this.offerService.getAllOfferTypes().subscribe((data) => {
      this.types = data;
    });
  }

  loadHistory(id: string) {
    this.offerService.getWorkflowHistory(id).subscribe((data) => {
      console.log(data);
      this.history = data
        .map((item: FeedbackResponse) => new FeedbackResponse(item))
        .sort(
          (a: FeedbackResponse, b: FeedbackResponse) =>
            b.changedAtDate.getTime() - a.changedAtDate.getTime(),
        );
    });
  }

  loadOffer(id: string) {
    this.offerService.getOfferById(id).subscribe((data) => {
      this.offer = data;
    });
  }

  getBadgeText(feedback: FeedbackResponse): string {
    if (!feedback) {
      return '???';
    }

    switch (feedback.newStatus) {
      case 'REJECTED':
        return 'Abgelehnt';
      case 'REQUEST_MODIFICATION':
        return 'Änderungen angefordert';
      case 'APPROVED':
        return 'Genehmigt';
      case 'NEW':
        return 'Angebot erstellt/editiert';
      default:
        return feedback.newStatus;
    }
  }

  getBadgeColor(feedback: FeedbackResponse): string {
    if (!feedback) {
      return 'primary';
    }
    switch (feedback.newStatus) {
      case 'REJECTED':
        return 'bg-red-500';
      case 'REQUEST_MODIFICATION':
        return 'bg-amber-500';
      case 'APPROVED':
        return 'bg-green-500';
      case 'NEW':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  }

  formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getCategoryName(id: string): string {
    return this.categories.find((c) => c.id === id)?.name || '???';
  }

  getTypeName(id: string): string {
    return this.types.find((t) => t.id === id)?.offerName || '???';
  }

  isExpandable(element: FeedbackResponse): boolean {
    return ['APPROVE', 'REJECT', 'REQUEST_MODIFICATION'].includes(
      element.newStatus,
    );
  }

  expandRow(element: FeedbackResponse): void {
    if (this.isExpandable(element)) {
      this.expandedElement = this.expandedElement === element ? null : element;
    }
  }

  goBack(): void {
    this.location.back();
  }
}
